import React, { useMemo } from "react";
import { GenericCardsStatistic } from "@ms/shared-components";

import {
  createStatisticData,
  calculateStatisticValues,
} from "./config/index";
import { createLogger } from "../../utils/logger";

const logger = createLogger("StoreRevenueMonitor.StatisticCards");

interface StatisticCardsProps {
  dimensionResults: any[];
  onCardClick: (item: any, event: React.MouseEvent) => void;
}

/**
 * 统计卡片组件（StatisticCards）
 *
 * 展示全局维度汇总数据（如门店数、房态等），数据来自 useAutoMonitor.dimensionResults。
 * 使用 React.memo 自定义比较，仅 dimensionResults 长度或首项 storeId 变化时重渲染，
 * 与门店列表组件完全隔离，避免统计卡片更新触发门店卡片重渲染。
 */
const StatisticCards: React.FC<StatisticCardsProps> = React.memo(
  ({ dimensionResults, onCardClick }) => {
    const statLogger = logger.createChild("render");

    const statisticValues = useMemo(() => {
      statLogger.debug("dimensionResults数量", dimensionResults.length);
      if (dimensionResults.length === 0) {
        statLogger.warn("dimensionResults 为空，返回默认值");
        return {};
      }
      const values = calculateStatisticValues(
        dimensionResults.map((r) => ({
          dimension: r.dimension,
          status: r.status,
          storeId: r.storeId,
        })),
      );
      statLogger.debug("计算得到的值", values);
      return values;
    }, [dimensionResults, statLogger]);

    const statisticData = useMemo(() => {
      return createStatisticData(onCardClick, statisticValues);
    }, [onCardClick, statisticValues]);

    return (
      <div className="stat-cards-container">
        <GenericCardsStatistic data={statisticData} columns={8} />
      </div>
    );
  },
  (prevProps, nextProps) => {
    const prevLength = prevProps.dimensionResults.length;
    const nextLength = nextProps.dimensionResults.length;
    const prevFirstStoreId =
      prevLength > 0 ? prevProps.dimensionResults[0]?.storeId : null;
    const nextFirstStoreId =
      nextLength > 0 ? nextProps.dimensionResults[0]?.storeId : null;
    const shouldUpdate =
      prevLength !== nextLength || prevFirstStoreId !== nextFirstStoreId;

    if (shouldUpdate) {
      const statLogger = logger.createChild("propsCompare");
      statLogger.debug("检测到 dimensionResults 变化，将重新渲染", {
        prevLength,
        nextLength,
        prevFirstStoreId,
        nextFirstStoreId,
      });
    }

    return !shouldUpdate;
  },
);

StatisticCards.displayName = "StatisticCards";

export default StatisticCards;
